import type { TranslationChange } from './translationsApi';

/** The columns before the languages, as the export writes them. */
const FIXED = ['kind', 'id', 'field', 'context', 'source'] as const;

/** Rows of an RFC 4180 CSV: quoted cells may hold commas, quotes ("") and line breaks. A byte order mark is skipped. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (quoted) {
      if (c === '"' && source[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"' && cell === '') quoted = true;
    else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && source[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** The export starts a cell a spreadsheet would run as a formula with `'`; the import takes it off. */
const unguard = (cell: string) => (/^'[=+\-@\t\r]/.test(cell) ? cell.slice(1) : cell);

export interface CsvChanges {
  /** One change per filled language cell; empty cells change nothing (remove a translation on the screen). */
  changes: TranslationChange[];
  /** The file line of each change, for the server's refusals. */
  lines: number[];
  /** The languages the file has columns for. */
  languages: string[];
}

/** Why a file can't be read: its first row isn't the export's header. */
export class CsvHeaderError extends Error {}

/** The translations in an exported (and edited) CSV. Lines are counted from 1, the header being line 1. */
export function toChanges(text: string): CsvChanges {
  const [header, ...rows] = parseCsv(text);
  if (!header || FIXED.some((name, i) => header[i]?.trim().toLowerCase() !== name) || header.length <= FIXED.length)
    throw new CsvHeaderError('header');
  const languages = header.slice(FIXED.length).map((l) => l.trim().toLowerCase());
  const changes: TranslationChange[] = [];
  const lines: number[] = [];
  rows.forEach((row, r) => {
    const [kind = '', id = '', field = ''] = row.map((c) => c.trim());
    languages.forEach((language, l) => {
      const value = unguard(row[FIXED.length + l] ?? '').trim();
      if (!value) return;
      changes.push({ kind, id, field, language, text: value });
      lines.push(r + 2);
    });
  });
  return { changes, lines, languages };
}

/** Splits a list into parts of at most `size`. */
export const chunk = <T,>(items: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));
